import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import CookieBanner from "./CookieBanner";
import SEOHead from "./SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const templateCache = {};

async function loadTemplate(page) {
  if (templateCache[page]) return templateCache[page];
  const res = await fetch(`/templates/${page}.html`);
  const text = await res.text();
  templateCache[page] = text;
  return text;
}

function applyContent(rootEl, content) {
  if (!rootEl || !content) return;
  const nodes = rootEl.querySelectorAll("[data-cms-id]");
  nodes.forEach((el) => {
    const id = el.getAttribute("data-cms-id");
    const c = content[id];
    if (!c) return;
    if (c.type === "img") {
      // For images: rewrite src and alt
      let src = c.value || "";
      // Uploaded images come as /uploads/... – prefix with backend URL
      if (src.startsWith("/uploads/")) src = BACKEND_URL + src;
      if (src) el.setAttribute("src", src);
      if (c.alt !== undefined) el.setAttribute("alt", c.alt || "");
    } else {
      // Text/HTML: replace innerHTML
      if (c.value !== undefined) {
        el.innerHTML = c.value;
      }
    }
  });
}

// Map a path like "/services/sales" or "/contact" to the template name we
// fetch from /templates/{name}.html. Mirrors the route table in App.js.
function pathToPage(path) {
  if (path === "/") return "home";
  if (path === "/about") return "about";
  if (path === "/services") return "services";
  if (path === "/services/sales") return "svc-sales";
  if (path === "/services/logistics") return "svc-logistics";
  if (path === "/services/data-analysis") return "svc-data-analysis";
  if (path === "/services/support") return "svc-support";
  if (path === "/services/credit") return "svc-credit";
  if (path === "/demos") return "demos";
  if (path === "/contact") return "contact";
  if (path === "/terms") return "terms";
  if (path === "/privacy") return "privacy";
  if (path === "/thank-you" || path === "/gracias") return "thank-you";
  return null;
}

function rewriteInternalLinks(rootEl, navigate) {
  if (!rootEl) return;
  const links = rootEl.querySelectorAll("a[href]");
  links.forEach((a) => {
    const href = a.getAttribute("href");
    if (!href) return;
    // Internal route?
    if (
      href === "/" ||
      href.startsWith("/about") ||
      href.startsWith("/services") ||
      href.startsWith("/demos") ||
      href.startsWith("/contact") ||
      href.startsWith("/terms") ||
      href.startsWith("/privacy") ||
      href.startsWith("/thank-you") ||
      href.startsWith("/gracias")
    ) {
      // Intercept clicks for SPA navigation
      a.onclick = (e) => {
        // Allow modifier keys to open in new tab
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      };
      // Prefetch the destination template on hover/focus so by the time the
      // user clicks, we already have the HTML in memory and the navigation
      // is effectively instant.
      const prefetch = () => {
        const target = pathToPage(href.split("?")[0].split("#")[0]);
        if (target) loadTemplate(target);
      };
      a.addEventListener("mouseenter", prefetch, { once: true });
      a.addEventListener("focus", prefetch, { once: true });
    }
  });
}

// Wire up the Webflow mobile hamburger menu. Webflow ships a script that
// handles this, but since the template is injected via dangerouslySetInnerHTML,
// scripts inside it never execute. We reimplement the toggle here.
function attachMobileNav(rootEl) {
  if (!rootEl) return;
  const button = rootEl.querySelector(".w-nav-button");
  const menu = rootEl.querySelector(".w-nav-menu");
  const nav = rootEl.querySelector(".w-nav");
  if (!button || !menu || button.__dosmass_bound) return;
  button.__dosmass_bound = true;

  const close = () => {
    button.classList.remove("w--open");
    menu.classList.remove("w--open");
    nav?.classList.remove("w--open");
    button.setAttribute("aria-expanded", "false");
    menu.style.display = "";
    document.body.style.overflow = "";
  };
  const open = () => {
    button.classList.add("w--open");
    menu.classList.add("w--open");
    nav?.classList.add("w--open");
    button.setAttribute("aria-expanded", "true");
    // Override Webflow's inline display:none (set by its stripped JS) on mobile
    menu.style.display = "block";
    document.body.style.overflow = "hidden";
  };

  button.setAttribute("role", "button");
  button.setAttribute("tabindex", "0");
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-label", "Toggle navigation");

  button.addEventListener("click", (e) => {
    e.preventDefault();
    if (button.classList.contains("w--open")) close();
    else open();
  });
  button.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      button.click();
    }
  });

  // Close the menu when any nav link inside it is clicked
  menu.querySelectorAll("a").forEach((a) => {
    a.addEventListener("click", () => close());
  });

  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && button.classList.contains("w--open")) close();
  });
}

function attachContactForm(rootEl, navigate, content, lang) {
  const form = rootEl && rootEl.querySelector("form.form-contacts");
  if (!form || form.__dosmass_bound) return;
  form.__dosmass_bound = true;
  form.setAttribute("novalidate", "novalidate");
  // Ensure action doesn't submit to Webflow
  form.setAttribute("method", "post");
  form.removeAttribute("action");

  const t = (k, f) => (content[k] && content[k].value) || f;

  // Build inline error node
  const errorBox = document.createElement("div");
  errorBox.setAttribute("data-testid", "contact-form-error");
  errorBox.style.cssText = "display:none;margin-top:12px;padding:12px 14px;border-radius:8px;background:rgba(239,68,68,0.08);color:#b91c1c;border:1px solid rgba(239,68,68,0.25);font-size:14px;font-family:inherit;";
  form.appendChild(errorBox);

  const showError = (msg) => { errorBox.textContent = msg; errorBox.style.display = "block"; };
  const hideError = () => { errorBox.style.display = "none"; };

  const submitBtns = form.querySelectorAll('[type="submit"]');

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    hideError();
    const fd = new FormData(form);
    const first = (fd.get("Nombre-2") || "").toString().trim();
    const last = (fd.get("apellido-2") || "").toString().trim();
    const email = (fd.get("email-2") || "").toString().trim();
    const phone = (fd.get("Tel-fono-2") || "").toString().trim();
    const message = (fd.get("Comentario-2") || "").toString().trim();

    if (!first || !last || !email || !phone || !message) {
      showError(t("contact.form.error_required", lang === "en" ? "Please fill in all required fields." : "Por favor completa todos los campos requeridos."));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError(t("contact.form.error_email", lang === "en" ? "Please enter a valid email address." : "Por favor ingresa un correo electrónico válido."));
      return;
    }
    if (!/^[+\d][\d\s\-().]{4,29}$/.test(phone)) {
      showError(t("contact.form.error_phone", lang === "en" ? "Please enter a valid phone number." : "Por favor ingresa un número de teléfono válido."));
      return;
    }

    // Disable buttons
    const origValues = [];
    submitBtns.forEach((b) => {
      origValues.push({ el: b, val: b.value || b.innerText, dis: b.disabled });
      b.disabled = true;
      const lbl = t("contact.form.submitting", lang === "en" ? "Sending…" : "Enviando…");
      if (b.tagName === "INPUT") b.value = lbl;
      else b.innerText = lbl;
    });

    try {
      const res = await fetch(`${API}/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: first, last_name: last, email, phone, message,
          lang, source_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        let detail = "";
        try { const j = await res.json(); detail = j.detail?.msg || j.detail || ""; } catch {}
        throw new Error(detail || "submit_failed");
      }
      form.reset();
      navigate("/thank-you");
    } catch (err) {
      const base = t("contact.form.error_generic", lang === "en" ? "We couldn't send your message. Please try again." : "No pudimos enviar tu mensaje. Inténtalo de nuevo.");
      showError(err.message && err.message !== "submit_failed" ? `${base} (${err.message})` : base);
      submitBtns.forEach((_, i) => {
        const o = origValues[i];
        o.el.disabled = o.dis;
        if (o.el.tagName === "INPUT") o.el.value = o.val;
        else o.el.innerText = o.val;
      });
    }
  });
}

export default function PublicPage({ page }) {
  const containerRef = useRef(null);
  const [html, setHtml] = useState("");
  const [content, setContent] = useState(null);
  // `loading` only governs the first-paint loader. After the first content
  // is applied, subsequent route/lang changes do NOT re-show the loader —
  // the previous page stays visible until the new one is ready, then they
  // swap atomically. This makes navigation feel instant (no white flash).
  const [loading, setLoading] = useState(true);
  const hasFirstPaintRef = useRef(false);
  const { lang, toggle } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  // Load HTML template once per page
  useEffect(() => {
    let mounted = true;
    loadTemplate(page).then((text) => {
      if (mounted) setHtml(text);
    });
    return () => {
      mounted = false;
    };
  }, [page]);

  // Load CMS content per (page, lang). Pages that share the Webflow chrome
  // (legal pages, service detail pages) also fetch the source page so the
  // shared header/footer get translated.
  useEffect(() => {
    let mounted = true;
    let pageParam = page;
    if (page === "terms" || page === "privacy") pageParam = `${page},contact`;
    else if (page.startsWith("svc-")) pageParam = `${page},services`;
    axios
      .get(`${API}/cms/content`, { params: { page: pageParam, lang } })
      .then((r) => {
        if (mounted) setContent(r.data.content || {});
      })
      .catch(() => {
        if (mounted) setContent({});
      });
  }, [page, lang]);

  // After both HTML and content are ready, inject into DOM synchronously
  // INSIDE useLayoutEffect (runs before browser paint on the same commit
  // cycle). This guarantees the user NEVER sees the raw Spanish template
  // before the CMS content is applied — no flash, no flicker, regardless
  // of lang, fetch speed, or navigation direction.
  const lastRenderKeyRef = useRef("");
  useLayoutEffect(() => {
    if (!html || !content || !containerRef.current) return;
    const container = containerRef.current;
    // Re-inject the template HTML only when page changed (a different
    // template). On lang toggles the template is the same; we just mutate
    // text nodes in place via applyContent — no innerHTML reset, so the
    // DOM never flashes back to raw Spanish.
    const renderKey = `${page}:${html.length}`;
    if (lastRenderKeyRef.current !== renderKey) {
      container.innerHTML = html;
      lastRenderKeyRef.current = renderKey;
    }
    applyContent(container, content);
    rewriteInternalLinks(container, navigate);
    attachMobileNav(container);
    if (page === "contact") attachContactForm(container, navigate, content, lang);
    // First-paint completed — hide the loader. After this we never re-show
    // it, even on subsequent route/lang changes, so navigation feels instant.
    if (!hasFirstPaintRef.current) {
      hasFirstPaintRef.current = true;
      setLoading(false);
    }
  }, [html, content, page, lang, navigate]);

  // Scroll to top only when the route actually changes (page or hash) —
  // NOT on language toggle. Otherwise toggling lang while scrolled down
  // yanks the user back to the top of the page.
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    // Skip the very first pass: the page already loads at scrollY=0, and
    // we don't want to fight the browser's scroll-restoration on mount.
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      return;
    }
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) target.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.hash, page]);

  return (
    <>
      <SEOHead page={page} />

      {/* Floating language switcher */}
      <button
        className="dosmass-lang-switcher"
        onClick={toggle}
        data-testid="lang-switcher"
        aria-label="Switch language"
      >
        {lang === "es" ? "EN" : "ES"}
      </button>

      {loading && (
        <div className="dosmass-loader" data-testid="page-loading">
          <span>Cargando…</span>
        </div>
      )}

      {/* Container is managed imperatively; we deliberately render it empty
          and let useLayoutEffect write the fully-hydrated HTML in one pass
          before the browser paints. */}
      <div
        ref={containerRef}
        data-testid={`public-page-${page}`}
      />

      <CookieBanner />
    </>
  );
}
